import { DataSource } from '@angular/cdk/table'
import { Injectable } from '@angular/core'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'
import { AsiakkaanTuote } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { BehaviorSubject, combineLatest, Observable, of as observableOf } from 'rxjs'
import { distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators'
import { KayttajaService } from '../../_angular/service/kayttaja.service'
import { suodataLajitteleJaSivuta } from '../rekisterin-listaus'
import { vertaaTuotteita } from './tuoterekisteri-logiikka'

interface Hakutiedot {
  sivutustiedot: PageEvent
  lajittelutiedot: Sort
}

@Injectable()
export class TuotteetFirestoreDataSource extends DataSource<AsiakkaanTuote> {
  private _hakutiedot = this._annaOletusHakutiedot()
  private _hakutiedotSubject = new BehaviorSubject<Hakutiedot>(this._hakutiedot)

  private _rivienMaaraSubject = new BehaviorSubject<number>(0)
  rivienMaaraObservable: Observable<number> = this._rivienMaaraSubject.asObservable()

  /** Rekisterin tuotteet, tai null latauksen ajan */
  private _kaikkiTuotteetObservable: Observable<AsiakkaanTuote[] | null>
  lataaObservable: Observable<boolean>
  asiakkaanTuotteetObservable: Observable<AsiakkaanTuote[]>

  constructor(
    private _kayttajaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _laskuUriService: LaskuUriService,
  ) {
    super()

    this._kayttajaService.kayttajanTiedotObservable.pipe(filter(kayttaja => !kayttaja)).subscribe(() => {
      this._hakutiedot = this._annaOletusHakutiedot()
      this._hakutiedotSubject.next(this._hakutiedot)
    })

    this._kaikkiTuotteetObservable = this._kayttajaService.kayttajanTiedotObservable.pipe(
      switchMap(kayttaja => {
        if (!kayttaja) {
          return observableOf<AsiakkaanTuote[]>([])
        }
        const uri = this._laskuUriService.getLaskuTuotteetCollectionUri(kayttaja.asiakasAvain)
        return this._firebaseLemonaid
          .firestoreCollection<AsiakkaanTuote>(uri)
          .listenSnapshots()
          .pipe(
            map(snapshots =>
              snapshots.map(snapshot => {
                const tuote = snapshot.data()
                tuote.$key = snapshot.id
                return tuote
              }),
            ),
            startWith<AsiakkaanTuote[] | null>(null),
          )
      }),
      lemonShare(),
    )

    this.lataaObservable = this._kaikkiTuotteetObservable.pipe(
      map(tuotteet => tuotteet === null),
      distinctUntilChanged(),
    )

    this.asiakkaanTuotteetObservable = combineLatest([this._kaikkiTuotteetObservable, this._hakutiedotSubject]).pipe(
      map(([tuotteet, hakutiedot]) => {
        if (tuotteet === null) {
          return []
        }
        const sivu = suodataLajitteleJaSivuta(tuotteet, hakutiedot.lajittelutiedot, hakutiedot.sivutustiedot, vertaaTuotteita)
        this._rivienMaaraSubject.next(sivu.rivienMaara)
        return sivu.rivit
      }),
      lemonShare(),
    )
  }

  private _annaOletusHakutiedot(): Hakutiedot {
    return {
      sivutustiedot: { length: 0, pageIndex: 0, pageSize: 20 },
      lajittelutiedot: { active: 'nimi', direction: 'asc' },
    }
  }

  getSort(): Sort {
    return this._hakutiedot.lajittelutiedot
  }

  getPage(): PageEvent {
    return this._hakutiedot.sivutustiedot
  }

  changeSort(sort: Sort) {
    this._hakutiedot = { ...this._hakutiedot, lajittelutiedot: sort, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  changePage(page: PageEvent) {
    this._hakutiedot = { ...this._hakutiedot, sivutustiedot: page }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  connect(): Observable<AsiakkaanTuote[]> {
    return this.asiakkaanTuotteetObservable
  }

  disconnect() {}
}
